import React from "react";

const labels = {
  "research.capture": "Save cited research in Notes",
  "plan.create": "Create plan and steps",
  "plan.update": "Update plan and steps",
  "plan.step_reconcile": "Update linked plan step",
  "task.create": "Create responsibility",
  "task.update": "Update responsibility",
  "task.reconcile": "Update tracked task",
  "task.state": "Update responsibility status",
  "person.create": "Add person",
  "person.update": "Update person",
  "person.log_contact": "Log actual contact",
  "contact.follow_up": "Update contact follow-up",
  "occasion.create": "Add occasion",
  "occasion.update": "Update occasion",
  "occasion.prepare": "Update occasion preparation",
  "discovery.create": "Schedule recurring research",
  "discovery.update": "Update recurring research",
  "calendar.create": "Create local calendar event",
  "calendar.update": "Update local calendar event",
  "calendar.cancel": "Cancel local calendar event",
  "note.create": "Save note",
  "note.append": "Append to note",
  "google_task.propose_state": "Prepare Google Task status and day for review",
  "outbox.draft": "Create unsent draft",
  "finance.account": "Add ledger account",
  "finance.record": "Record ledger entry",
  "finance.reconcile": "Compare with statement",
  "vehicle.create": "Add vehicle",
  "vehicle.update": "Update vehicle",
  "vehicle.plan": "Add service schedule",
  "vehicle.update_plan": "Update service schedule",
  "vehicle.reading": "Record odometer reading",
  "vehicle.service": "Log completed service",
  "routine.create": "Add routine",
  "routine.update": "Update routine",
  "routine.log": "Record actual activity",
  "routine.decide": "Update planned occurrence",
  "routine.reschedule": "Move planned occurrence",
  "routine.briefing": "Configure routine preparation briefings",
  "meeting.configure": "Update meeting agenda and outcome",
  "meeting.capture": "Save linked meeting notes",
  "meeting.action": "Track meeting commitment",
  "meeting.review": "Update meeting review status",
  "meeting.draft": "Create unsent meeting follow-up",
  "tracker.propose_update": "Prepare Google Sheets update for review",
  "tracker.followup": "Prepare unsent tracker follow-up",
  "mail.reply": "Prepare unsent email reply",
  "mail.watch": "Track email follow-up",
  "mail.link_task": "Link email and existing task",
  "calendar.propose_update": "Prepare Google Calendar changes for review",
  "calendar.propose_cancel": "Prepare Google Calendar cancellation for review",
  "calendar.propose_response": "Prepare invitation response for review",
  "calendar.propose_series_update": "Prepare whole-series changes for review",
  "calendar.propose_series_schedule": "Preview whole-series scheduling for review",
  "calendar.propose_following_cancel": "Preview ending this and following meetings",
  "calendar.propose_following": "Preview this and following meetings for review",
  "calendar.propose_series_cancel": "Prepare whole-series cancellation for review",
};
const fields = {
  account_id: "Account / from",
  counter_account_id: "To account",
  person_id: "Person",
  context_id: "Shared context",
  task_id: "Existing task",
  vehicle_id: "Vehicle",
  plan_id: "Service schedule",
  routine_id: "Routine",
  occurrence_id: "Planned occurrence",
  baseline_count: "Previously completed sessions",
  baseline_as_of: "Previous count through date",
  milestones: "Progress milestones",
  topics: "Public research topics",
  podcast_feeds: 'Selected podcast feeds',
  needed_by: "Needed by (account local time)",
  useful_for_minutes: "Useful for minutes after needed-by",
  repayment_of: "Original borrowing",
  due_at: "Due",
  days_before_target: "Days before target date",
  scheduled_date: "Google scheduled day",
  occurred_at: "Contact time",
  occurred_on: "Transaction date",
  opened_on: "Opening date",
  start_time: "Start",
  end_time: "End",
  check_in_days: "Check in every (days)",
  local_time: "Local preparation time",
  lead_minutes: "Remind ahead (minutes)",
  track_repayment: "Track repayment",
  target_id: "Document / page",
  expected_due_at: "Occurrence being completed",
  as_of: "Statement as-of date",
};
function showValue(key, value, timezone) {
  if (value === null || value === "" || (Array.isArray(value) && !value.length))
    return "Clear value";
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T/.test(value) &&
    !Number.isNaN(Date.parse(value))
  ) {
    return (
      new Date(value).toLocaleString(undefined, {
        timeZone: timezone || "UTC",
        dateStyle: "medium",
        timeStyle: "short",
      }) + ` (${timezone || "UTC"})`
    );
  }
  if (key === "definition") return JSON.stringify(value, null, 2);
  if (key === "weekdays")
    return value
      .map(
        (day) =>
          [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ][day],
      )
      .join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key === "milestones")
    return value
      .map(
        (item) =>
          `After ${item.after_sessions} completed: ${item.title}${item.notes ? ` — ${item.notes}` : ""}`,
      )
      .join("\n");
  if (key === "topics")
    return value.map(item => `From ${item.after_sessions} completed sessions: ${item.query}`).join("\n");
  if (Array.isArray(value))
    return value
      .map((item) =>
        typeof item === "object" ? JSON.stringify(item, null, 2) : String(item),
      )
      .join(", ");
  if (typeof value === "object")
    return Object.entries(value)
      .map(([name, amount]) => `${name.replaceAll("_", " ")}: ${amount}`)
      .join(", ");
  return String(value);
}

export function ProposedAction({
  action,
  timezone,
  evidenceLabel = "Request excerpt",
}) {
  const schedulingSeries = action.operation === "calendar.propose_series_schedule";
  const following = action.operation === "calendar.propose_following";
  const googleTask = action.operation === "google_task.propose_state";
  const mailWork = action.operation === "mail.link_task";
  const contextualFields = {
    occurred_at:
      action.operation === "routine.log" ? "Activity started" : "Contact time",
    occurred_on:
      action.operation === "vehicle.service"
        ? "Service date"
        : "Transaction date",
    local_time: action.operation === "routine.briefing"
      ? `Research time (${timezone || "account timezone"})`
      : action.operation.startsWith("routine.")
      ? "Routine time"
      : "Local preparation time",
    enabled: action.operation === "routine.briefing" ? "Briefings enabled" : "Enabled",
    linked: "Keep conversation linked",
    weekdays: action.operation === "routine.briefing" ? "Research weekdays" : "Weekdays",
  };
  const values =
    following ? { ...action.values.schedule, ...action.values.changes } : mailWork ? {task_id: action.values.task_id, linked: action.values.linked} : schedulingSeries
      ? action.values.schedule
      : action.operation === "meeting.action"
      ? action.values.task
      : ["tracker.propose_update", "calendar.propose_update", "calendar.propose_series_update"].includes(
            action.operation,
          )
        ? {
            ...(action.operation === "tracker.propose_update"
              ? { external_id: action.values.external_id }
              : {}),
            ...action.values.changes,
          }
        : action.values;
  return (
    <article className="rounded-md border border-[#e6dfcb] bg-white p-3">
      <h4 className="text-sm font-semibold">
        {mailWork ? (action.values.linked ? "Link email to existing task" : "Unlink email from task") : labels[action.operation] || action.operation}
        {action.target_label ? ` · ${action.target_label}` : ""}
      </h4>
      {mailWork && <div className="mt-2 space-y-1 text-xs text-[#68746d]">
        <p>Mailbox: {action.mail_work?.mailbox || "Selected conversation"}</p>
        <p>{action.mail_work?.currently_linked ? "Currently linked" : "Currently unlinked"}</p>
        {action.mail_work?.task && <p>Current task: {action.mail_work.task.status} · {action.mail_work.task.priority} · {action.mail_work.task.owner || "No owner"} · {action.mail_work.task.due_at ? showValue('due_at', action.mail_work.task.due_at, timezone) : "No deadline set"}</p>}
      </div>}
      {["calendar.propose_series_update", "calendar.propose_series_cancel", "calendar.propose_series_schedule"].includes(action.operation) && (
        <p className="mt-2 text-sm">This proposal covers the entire recurring series, including past and future meetings. Review the exact Google change separately before sending.</p>
      )}
      {following && <p className="mt-2 text-sm">Earlier meetings remain in the old series. Future exceptions, extra dates and exclusions reset in the replacement. This creates a local preview; review the boundary and dates separately before sending Google changes.</p>}
      {action.operation === "calendar.propose_following_cancel" && <p className="mt-2 text-sm">End the series from this occurrence’s original position. Earlier meetings, notes and commitments remain. This creates a local ending review; apply it separately in the calendar workspace.</p>}
      {["task.reconcile", "plan.step_reconcile"].includes(action.operation) ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <caption className="sr-only">{action.operation === "plan.step_reconcile" ? "Plan step changes" : "Tracked task changes"}</caption>
            <thead><tr><th className="p-2">Field</th><th className="p-2">Before</th><th className="p-2">After</th></tr></thead>
            <tbody>{Object.entries(action.values.changes).map(([key, value]) => (
              <tr key={key} className="border-t border-[#e6dfcb]">
                <th className="p-2 font-semibold capitalize">{fields[key] || key.replaceAll("_", " ")}</th>
                <td className="p-2 break-words">{action.before?.[key] == null || action.before[key] === "" ? "Not set" : showValue(key, action.before[key], timezone)}</td>
                <td className="p-2 break-words">{showValue(key, value, timezone)}</td>
              </tr>
            ))}</tbody>
          </table>
          <p className="mt-3 text-xs text-[#68746d]">{action.operation === "plan.step_reconcile" ? "Updates this step and its reminders in Timelyzer. Prerequisites and history are retained. Saving this record does not book, pay, enroll or send a message." : "Applies to the existing task in Timelyzer. Its source link and history are retained."}</p>
        </div>
      ) : <dl className="mt-3 space-y-2 text-xs">
        {Object.entries(values)
          .filter(
            ([key, value]) =>
              key !== "request_id" &&
              key !== "expected_revision" &&
              (action.changed_fields?.includes(key) ||
                (value !== null &&
                  value !== "" &&
                  !(Array.isArray(value) && !value.length) &&
                  !(typeof value === "object" && !Object.keys(value).length))),
          )
          .map(([key, value]) => (
            <div
              key={key}
              className="grid grid-cols-[minmax(90px,1fr)_2fr] gap-3"
            >
              <dt className="capitalize text-[#68746d]">
                {contextualFields[key] ||
                  fields[key] ||
                  key.replaceAll("_", " ")}
              </dt>
              <dd className="break-words whitespace-pre-wrap">
                {schedulingSeries && ["start", "end"].includes(key)
                  ? `${value.replace("T", " ")} (${values.time_zone}${values.all_day && key === "end" ? "; exclusive" : ""})`
                  : googleTask && key === "status" ? ({completed: "Completed", needsAction: "Needs action"})[value]
                  : action.references?.[key] || showValue(key, value, timezone)}
              </dd>
            </div>
          ))}
      </dl>}
      {schedulingSeries && <p className="mt-3 text-xs text-[#68746d]">{values.pattern ? "This replaces the current repeat rules and additional or excluded dates." : "Keep the current repeat rules and additional or excluded dates."} Dates and availability will be previewed before any Google change.</p>}
      {googleTask && <p className="mt-3 text-xs text-[#68746d]">Creates a review in the linked note. Review and apply it there to change Google Tasks. Unspecified fields keep their current Google values. The scheduled day has no time; your note reminder and other commitments remain separate.</p>}
      {mailWork && <p className="mt-3 text-xs text-[#68746d]">{action.values.linked ? "Makes this existing task available when the conversation is analysed. Proposed task updates require review. This does not enable analysis or send email." : "Removes the conversation link. The task, its deadline and applied history remain saved."}</p>}
      {action.operation === "routine.briefing" && <p className="mt-3 text-xs text-[#68746d]">
        {values.enabled === false ? "These briefings will be paused. Topics and the research schedule remain saved. " : "These public topics will be used for web research when monitoring and the worker are running. "}
        Recorded activity selects the topic; class rescheduling does not move this research schedule.
        {values.needed_by && values.local_time && ` Needed by ${values.needed_by}${values.needed_by < values.local_time ? " the following day" : " on the research day"}.`}
      </p>}
      <details className="mt-3 text-xs text-[#68746d]">
        <summary>{evidenceLabel}</summary>
        <p className="mt-2 whitespace-pre-wrap">{action.evidence}</p>
      </details>
    </article>
  );
}

export default function ChatActionPlan({ turn, busy, decide }) {
  if (turn?.status !== "pending") return null;
  const financial = turn.proposed_actions.some((action) =>
    action.operation.startsWith("finance."),
  );
  const trackerUpdate = turn.proposed_actions.some(
    (action) => action.operation === "tracker.propose_update",
  );
  const calendarChange = turn.proposed_actions.some((action) =>
    ["calendar.propose_update", "calendar.propose_cancel", "calendar.propose_response", "calendar.propose_series_update", "calendar.propose_series_cancel", "calendar.propose_series_schedule", "calendar.propose_following", "calendar.propose_following_cancel"].includes(
      action.operation,
    ),
  );
  return (
    <section
      className="space-y-3 rounded-lg border border-[#dfd5b4] bg-[#fffdf4] p-4"
      aria-label="Proposed changes"
    >
      <h3 className="text-sm font-bold">Review proposed changes</h3>
      {turn.proposed_actions.map((action, index) => (
        <ProposedAction
          key={index}
          action={action}
          timezone={turn.context?.timezone}
        />
      ))}
      <p className="text-xs leading-5 text-[#68746d]">
        {financial
          ? "These entries update your recorded ledger. They do not move real funds. "
          : ""}
        Nothing in this plan has been applied. Outgoing messages and document
        updates remain drafts in Outbox.
        {trackerUpdate &&
          " Sheet updates are prepared in Work trackers for a separate review and apply step."}
        {calendarChange &&
          " Calendar proposals require a separate review and apply step in Google Calendar changes. Preparing them sends no notifications."}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          className="primary-button text-xs"
          disabled={busy}
          onClick={() => decide(turn, "apply")}
        >
          {financial
            ? "Record reviewed financial changes"
            : "Apply reviewed changes"}
        </button>
        <button
          className="secondary-button text-xs"
          disabled={busy}
          onClick={() => decide(turn, "cancel")}
        >
          Cancel proposed changes
        </button>
      </div>
    </section>
  );
}
